import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import Card from '../common/Card';

const CategoryPieChart = ({ transactions, categories, periodLabel }) => {
  const svgRef = useRef(null);

  const { data, totalExpenses } = React.useMemo(() => {
    if (!transactions || !categories) return { data: [], totalExpenses: 0 };

    const includedIds = new Set(
      categories.filter(c => Boolean(c.include_in_analytics)).map(c => c.id)
    );
    const colorById = new Map(categories.map(c => [c.id, c.color || null]));

    const categoryTotals = {};

    transactions.forEach(t => {
      const sign = t.amount < 0 ? -1 : 1;
      const amount = Math.abs(t.amount);
      t.categories.forEach(cat => {
        if (!includedIds.has(cat.id)) return;

        let catAmount = amount;
        if (t.categories.length > 1 && cat.split_amount) {
          catAmount = Math.abs(cat.split_amount);
        } else if (t.categories.length > 1 && !cat.split_amount) {
          catAmount = amount / t.categories.length;
        }

        if (!categoryTotals[cat.id]) {
          categoryTotals[cat.id] = {
            name: cat.name,
            icon_name: cat.icon_name,
            net: 0,
            overrideColor: colorById.get(cat.id) || null,
            id: cat.id
          };
        }
        categoryTotals[cat.id].net += sign * catAmount;
      });
    });

    const sorted = Object.values(categoryTotals)
      .map(({ net, ...rest }) => ({ ...rest, value: Math.max(0, -net) }))
      .filter(e => e.value > 0)
      .sort((a, b) => b.value - a.value);

    const autoColor = d3.scaleOrdinal()
      .domain(sorted.map(e => e.id))
      .range(d3.schemePaired);

    const entries = sorted.map(e => ({ ...e, color: e.overrideColor || autoColor(e.id) }));

    return {
      data: entries,
      totalExpenses: entries.reduce((sum, e) => sum + e.value, 0)
    };
  }, [transactions, categories]);

  useEffect(() => {
    if (data.length === 0) return;

    const width = 280;
    const height = 280;
    const radius = Math.min(width, height) / 2;

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${width / 2},${height / 2})`);

    const pie = d3.pie()
      .value(d => d.value)
      .sort(null);

    const arc = d3.arc()
      .innerRadius(radius * 0.62)
      .outerRadius(radius * 0.95);

    const tooltip = d3.select("body")
      .append("div")
      .attr("class", "d3-tooltip")
      .style("position", "absolute")
      .style("visibility", "hidden")
      .style("background-color", "white")
      .style("border", "1px solid #e2e8f0")
      .style("border-radius", "0.75rem")
      .style("padding", "0.5rem 0.75rem")
      .style("box-shadow", "0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.02)")
      .style("font-size", "0.75rem")
      .style("font-weight", "600")
      .style("pointer-events", "none")
      .style("z-index", 1000);

    const arcs = svg.selectAll("arc")
      .data(pie(data))
      .enter()
      .append("g")
      .attr("class", "arc");

    arcs.append("path")
      .attr("d", arc)
      .attr("fill", (d) => d.data.color)
      .attr("stroke", "white")
      .style("stroke-width", "3px")
      .style("opacity", 0.9)
      .style("cursor", "pointer")
      .on('mouseover', function (event, d) {
        d3.select(this).transition()
          .duration(200)
          .attr('d', d3.arc().innerRadius(radius * 0.62).outerRadius(radius * 1.02))
          .style("opacity", 1);

        tooltip.style("visibility", "visible")
          .html(`
            <div class="text-neutral-900">${d.data.name}</div>
            <div class="text-neutral-500 font-mono mt-0.5">${d.data.value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</div>
          `);
      })
      .on('mousemove', function (event) {
        tooltip
          .style("top", (event.pageY - 10) + "px")
          .style("left", (event.pageX + 10) + "px");
      })
      .on('mouseout', function () {
        d3.select(this).transition()
          .duration(200)
          .attr('d', arc)
          .style("opacity", 0.9);

        tooltip.style("visibility", "hidden");
      });

    svg.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "-0.4em")
      .text("Ausgaben")
      .style("font-size", "11px")
      .style("fill", "#94a3b8")
      .style("font-weight", "700")
      .style("text-transform", "uppercase")
      .style("letter-spacing", "0.05em");

    svg.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "0.8em")
      .text(totalExpenses.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }))
      .style("font-size", "20px")
      .style("fill", "#1e293b")
      .style("font-weight", "800");

    return () => {
      tooltip.remove();
    };
  }, [data, totalExpenses]);

  const hasExpenses = data.length > 0;
  if (!transactions || transactions.length === 0 || !hasExpenses) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-neutral-50 rounded-2xl border border-neutral-100 h-[280px]">
        <span className="text-neutral-400 text-sm font-medium">Keine kategorisierten Ausgaben in diesem Zeitraum</span>
      </div>
    );
  }

  return (
    <Card className="p-6 shadow-sm flex flex-col">
      <h3 className="text-neutral-400 font-bold text-sm uppercase tracking-wider mb-6">
        Ausgaben {periodLabel || 'in diesem Zeitraum'}
      </h3>
      
      {/* Pie Chart */}
      <div className="flex flex-col items-center mb-4">
        <div className="relative overflow-visible">
          <svg ref={svgRef} className="overflow-visible"></svg>
        </div>
      </div>

      {/* Table View */}
      <div className="w-full">
        <table className="w-full text-sm">
          <tbody>
            {data.map((item) => (
              <tr key={item.id} className="border-b border-neutral-50 hover:bg-neutral-50 transition-all">
                <td className="py-2 px-1">
                  <div className="flex items-center space-x-2.5">
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }}></span>
                    <span className="text-neutral-700 font-medium">{item.name}</span>
                  </div>
                </td>
                <td className="text-right py-2 px-1 font-bold text-neutral-800 tabular-nums">
                  {item.value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                </td>
                <td className="text-right py-2 px-1 text-neutral-400 font-semibold tabular-nums">
                  {((item.value / totalExpenses) * 100).toFixed(1)}%
                </td>
              </tr>
            ))}
            <tr className="bg-neutral-50 font-bold rounded-b-lg">
              <td className="py-1 px-1 text-neutral-600 text-right">Gesamt</td>
              <td className="text-right py-1 px-1 text-neutral-900 tabular-nums">
                {totalExpenses.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
              </td>
              <td className="text-right py-1 px-1"></td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default CategoryPieChart;
